import React, { useState, useEffect } from 'react';
import { useRouter } from '../../../hooks/router';
import { XOverlay } from '../../../components/x/overlay/XOverlay';
import { PaymentProvider, Payment, PaymentStatus } from '../../../XV2';
import { AppCache } from '../../../services/app-cache';
import { apiProviderAdyenCheckoutHandleRedirectResultPost } from '../../../api/provider.adyen.checkout.handleRedirectResult.[post]';
import { XRouterRedirect } from '../../../components/x/router/redirect/XRouterRedirect';
import { RootIdPage } from '../../id/RootIdPage';
import { useAsync } from '@orderx/lab/ui';
import { useCustomerOrders } from '../../../hooks/customer-orders';
import { apiMeGetPaymentPost } from '../../../api/me.getPayment.[post]';
import { OrderIdPage } from '../../order/id/OrderIdPage';
import { Redirect } from 'react-router-dom';

interface Query {
    provider: PaymentProvider;
    zoneHash: string;
    orderId: string;
    redirectUrl?: string;
    resultCode: 'authorised' | string;
}

export function PaymentReturnPage() {
    const customerOrders = useCustomerOrders();
    const { query } = useRouter();
    const { zoneHash } = query as Query;
    const [isTimeoutPassed, setIsTimeoutPassed] = useState(false);
    const [checkoutResponse] = useState(AppCache.get('checkoutResponse'));

    const getPayment = useAsync(({ paymentId }) => apiMeGetPaymentPost({ body: { paymentId } }));

    const handleRedirect = useAsync(
        async () => {
            const result = await apiProviderAdyenCheckoutHandleRedirectResultPost({
                body: {
                    ...query,
                    checkoutResponse,
                    paymentData: checkoutResponse?.data?.paymentData,
                },
            });

            setTimeout(() => {
                setIsTimeoutPassed(true);
            }, 3000);

            if (result.orderGuid) {
                customerOrders.cacheOrderGuid(result.orderGuid);
            }

            return result;
        },
        {
            runOnInit: true,
        },
    );

    const paymentId = handleRedirect.result?.paymentId;
    const isError = handleRedirect.result?.type === 'ERROR';
    const orderId = handleRedirect.result?.orderId || checkoutResponse?.orderId;
    const payment: Pick<Payment, 'status'> = getPayment.result?.payment;
    const redirectTypeTable = handleRedirect.result?.redirectType === 'TABLE';
    const zoneId = handleRedirect.result?.zoneId;

    useEffect(() => {
        if (paymentId) {
            getPayment.run({ paymentId });

            const interval = setInterval(() => {
                getPayment.run({ paymentId });
            }, 2000);

            return () => clearInterval(interval);
        }
    }, [paymentId]);

    React.useEffect(() => {
        if (isTimeoutPassed && redirectTypeTable && zoneId) {
            window.location.href = `/pay/${zoneId}`;
        }
    }, [isTimeoutPassed, redirectTypeTable, zoneId]);

    if (isTimeoutPassed && redirectTypeTable && zoneId) {
        return <XOverlay type="loading" />;
    }

    if (isError) {
        if (isTimeoutPassed) {
            return <XRouterRedirect pageOrPath={RootIdPage} params={{ id: zoneHash }} />;
        }

        return <XOverlay type="error" title="Payment failed!" description="Something went wrong..." />;
    }

    if (payment) {
        if (payment.status === PaymentStatus.SETTLED) {
            if (isTimeoutPassed) {
                if (orderId) {
                    return <XRouterRedirect pageOrPath={OrderIdPage} params={{ orderId }} />;
                }

                return <XRouterRedirect pageOrPath={RootIdPage} params={{ id: zoneHash }} />;
            }

            return (
                <XOverlay
                    type="success"
                    title="Payment successful!"
                    description="You will be redirected in a few seconds"
                />
            );
        }
    }

    return <XOverlay type="loading" />;
}

PaymentReturnPage.route = '/payment-return';
